import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/cart-shopping-carts/my-cart/{session_id}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetCartInput = paths[url]['get']['parameters']['query'];
export type GetCartSuccessResponse = paths[url]['get']['responses']['200']['content']['application/json'];

/**
 * Requests endpoint to get all stores.
 * Then saves it in Data Context.
 */
export const useGetCart = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { login, setCart } = useData();
    const [loading, setLoading] = useState<boolean>(false);

    const sessionId = getSessionId();
    const commonQueryString = 'language=' + getCommonParameters().language;

    const request = async (props: GetCartInput) => {
        setLoading(true);

        try {
            const response = await fetch(
                `${API_URL}${ENDPOINT_URL.replace('/{session_id}', login === null ? '/' + sessionId : '?with_shipping=' + Boolean(props?.with_shipping))}&${commonQueryString}`,
                {
                    method: 'GET',
                    headers: getHeaders(),
                }
            );

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetCartSuccessResponse = await response.json();

            setCart(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);

            if (error.statusCode === 404) {
                // reset cart data
                setCart(null);
            }
        } finally {
            setLoading(false);
        }
    };

    return { request, loading };
};
